import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Globe, ExternalLink, ChevronLeft, ChevronRight, Plus, BarChart2, Trash2, AlertTriangle, CheckCircle } from "lucide-react"; 
import pageService from "../../../services/pageService";

// Utility function to format bytes to human readable format
const formatBytes = (bytes, decimals = 1) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

// Helper functions for performance score calculation
const calculateFCPScore = (firstPaint) => {
  if (!firstPaint) return 0;
  // Values are based on Lighthouse scoring curve for FCP
  if (firstPaint <= 1000) return 1; // Good
  if (firstPaint <= 2500) return 0.9 - 0.8 * ((firstPaint - 1000) / 1500);  // Average
  // Poor
  return Math.max(0, 0.1 - 0.1 * ((firstPaint - 2500) / 1500));
};

const calculateLCPScore = (largestPaint) => {
  if (!largestPaint) return 0;
  // Values are based on Lighthouse scoring curve for LCP
  if (largestPaint <= 1200) return 1; // Good
  if (largestPaint <= 2400) return 0.9 - 0.8 * ((largestPaint - 1200) / 1200);  // Average
  // Poor
  return Math.max(0, 0.1 - 0.1 * ((largestPaint - 2400) / 1200));
};

const calculateTBTScore = (cpuLoad) => {
  if (cpuLoad === undefined || cpuLoad === null) return 0.5; // Default to average if no data
  // Simplified scoring based on CPU load percentage
  if (cpuLoad <= 20) return 1; // Good
  if (cpuLoad <= 50) return 0.9 - 0.8 * ((cpuLoad - 20) / 30);  // Average
  // Poor
  return Math.max(0, 0.1 - 0.1 * ((cpuLoad - 50) / 50));
};

const calculateSIScore = (loadTime) => {
  if (!loadTime) return 0;
  // Values are based on Lighthouse scoring curve for Speed Index
  if (loadTime <= 2000) return 1; // Good
  if (loadTime <= 5800) return 0.9 - 0.8 * ((loadTime - 2000) / 3800);  // Average
  // Poor
  return Math.max(0, 0.1 - 0.1 * ((loadTime - 5800) / 3800));
};

const calculateTTIScore = (loadTime) => {
  if (!loadTime) return 0;
  // Values are based on Lighthouse scoring curve for TTI
  if (loadTime <= 2500) return 1; // Good
  if (loadTime <= 5500) return 0.9 - 0.8 * ((loadTime - 2500) / 3000);  // Average
  // Poor
  return Math.max(0, 0.1 - 0.1 * ((loadTime - 5500) / 3000));
};

const calculatePerformanceScore = (metrics) => {
  if (!metrics || !metrics.first_paint || !metrics.load_time) return null;
  
  // Define metric weights as per Lighthouse
  const weights = {
    FCP: 0.10,  // First Contentful Paint
    LCP: 0.25,  // Largest Contentful Paint (using first_paint as a proxy)
    TBT: 0.30,  // Total Blocking Time
    CLS: 0.15,  // Cumulative Layout Shift (assuming 0)
    SI: 0.10,   // Speed Index
    TTI: 0.10   // Time to Interactive (using load_time as a proxy)
  };
  
  // Calculate the scores for each metric using the Lighthouse scoring approach
  const fcpScore = calculateFCPScore(metrics.first_paint);
  const lcpScore = calculateLCPScore(metrics.first_paint); // Using first_paint as proxy for LCP
  const tbtScore = calculateTBTScore(metrics.cpu_load_percent); // Using CPU load as a proxy for TBT
  const clsScore = 1.0; // Assuming CLS is great (1.0)
  const siScore = calculateSIScore(metrics.load_time);
  const ttiScore = calculateTTIScore(metrics.load_time);
  
  // Calculate weighted average
  const weightedScore = 
    weights.FCP * fcpScore +
    weights.LCP * lcpScore +
    weights.TBT * tbtScore +
    weights.CLS * clsScore +
    weights.SI * siScore +
    weights.TTI * ttiScore;
  
  // Return a score between 0-100
  return Math.round(weightedScore * 100);
};

const WebsitePages = ({ 
  website, 
  runPageAnalysis, 
  pageSpeedAnalysis, 
  formatDate,
  showToast
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [activeDeviceType, setActiveDeviceType] = useState('desktop'); // 'desktop' or 'mobile'
  const [newPage, setNewPage] = useState({ url: '', title: '', description: '' });
  const [errors, setErrors] = useState({});
  const [pages, setPages] = useState([]);
  const [pagesWithPerformanceData, setPagesWithPerformanceData] = useState([]);
  const [allPagesForValidation, setAllPagesForValidation] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPageSpeedData, setIsLoadingPageSpeedData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit] = useState(10);

  // Function to fetch all website pages for validation (without pagination)
  const fetchAllPagesForValidation = useCallback(async () => {
    if (!website?.id) return;
    
    try {
      const response = await pageService.getAllPages({ 
        website_id: website.id,
        limit: 1000 // Use a large limit to effectively get all pages
      });
      
      if (response.success && response.pages) {
        setAllPagesForValidation(response.pages);
      } else {
        console.error("Failed to fetch all website pages for validation:", response);
      }
    } catch (error) {
      console.error("Error fetching all pages for validation:", error);
    }
  }, [website?.id]);

  // New function to fetch all page speeds for website in a single API call
  const fetchAllPageSpeedData = useCallback(async (fetchedPages) => {
    if (!website?.id || !fetchedPages.length) return;
    
    // Set loading state for PageSpeed data specifically
    setIsLoadingPageSpeedData(true);
    
    try {
      const response = await pageService.getAllPageSpeedsForWebsite(website.id);

      console.log("Fetched all page speeds:", response);  
      
      if (response.success && response.pageSpeeds) {
        // Create maps to store the most recent desktop and mobile data for each page
        const desktopDataMap = {};
        const mobileDataMap = {};
        
        // Filter out entries with null page_id or deleted_at not null
        response.pageSpeeds
          .filter(speedData => speedData.page_id && speedData.deleted_at === null)
          .forEach(speedData => {
            const pageId = speedData.page_id;
            // Check is_mobile as either 1 (integer) or true (boolean)
            const isMobile = speedData.is_mobile === 1 || speedData.is_mobile === true;
            const analyzedAt = new Date(speedData.analyzed_at || 0);
            
            // Store data in the appropriate map based on device type
            if (isMobile) {
              // For mobile data
              if (!mobileDataMap[pageId] || new Date(mobileDataMap[pageId].analyzed_at || 0) < analyzedAt) {
                mobileDataMap[pageId] = speedData;
              }
            } else {
              // For desktop data
              if (!desktopDataMap[pageId] || new Date(desktopDataMap[pageId].analyzed_at || 0) < analyzedAt) {
                desktopDataMap[pageId] = speedData;
              }
            }
          });
        
        console.log("Mobile data map:", mobileDataMap);
        console.log("Desktop data map:", desktopDataMap);
        
        // Map the performance data to each page
        const enhancedPages = fetchedPages.map(page => {
          // Get desktop and mobile data for this page
          const desktopData = desktopDataMap[page.id];
          const mobileData = mobileDataMap[page.id];
          
          // Parse raw metrics for desktop and mobile data
          const parseMetrics = (data) => {
            if (!data || !data.raw_metrics) return null;
            try {
              return typeof data.raw_metrics === 'string' ? 
                JSON.parse(data.raw_metrics) : data.raw_metrics;
            } catch (e) {
              console.error("Error parsing metrics:", e);
              return null;
            }
          };
          
          const desktopMetrics = parseMetrics(desktopData);
          const mobileMetrics = parseMetrics(mobileData);
          
          // Calculate performance scores using the provided function instead of using API values
          const desktopScore = desktopMetrics ? calculatePerformanceScore(desktopMetrics) : null;
          const mobileScore = mobileMetrics ? calculatePerformanceScore(mobileMetrics) : null;
          
          // Combine data into a single page object
          return {
            ...page,
            // Default to desktop data for the main display
            performance_score: desktopScore || mobileScore || null,
            last_analyzed_at: desktopData?.analyzed_at || mobileData?.analyzed_at || null,
            raw_metrics: desktopData?.raw_metrics || mobileData?.raw_metrics || null,
            raw_data: desktopData?.raw_data || mobileData?.raw_data || null,
            // Store both sets of data separately
            desktop_data: desktopData ? {
              performance_score: desktopScore,  // Use calculated score instead of API value
              last_analyzed_at: desktopData.analyzed_at,
              raw_metrics: desktopData.raw_metrics,
              raw_data: desktopData.raw_data
            } : null,
            mobile_data: mobileData ? {
              performance_score: mobileScore,  // Use calculated score instead of API value
              last_analyzed_at: mobileData.analyzed_at, 
              raw_metrics: mobileData.raw_metrics,
              raw_data: mobileData.raw_data
            } : null
          };
        });
        
        setPagesWithPerformanceData(enhancedPages);
      } else {
        // If API call fails, just use the pages without performance data
        setPagesWithPerformanceData(fetchedPages);
      }
    } catch (error) {
      console.error("Error fetching all page speed data:", error);
      // Fall back to pages without performance data
      setPagesWithPerformanceData(fetchedPages);
    } finally {
      // Clear loading state
      setIsLoadingPageSpeedData(false);
    }
  }, [website?.id]);

  // Function to fetch website pages with pagination
  const fetchPages = useCallback(async (pageNum) => {
    if (!website?.id) return;
    
    setIsLoading(true);
    try {
      const offset = (pageNum - 1) * limit;
      const response = await pageService.getWebsitePages(website.id, { limit, offset });
      
      if (response.success) {
        const fetchedPages = response.pages || [];
        setPages(fetchedPages);
        setTotalPages(Math.ceil((response.total || 0) / limit));
        setTotalItems(response.total || 0);
        setCurrentPage(pageNum);
        
        // After fetching pages, get all their performance data in a single API call
        await fetchAllPageSpeedData(fetchedPages);
      } else {
        console.error("Failed to fetch website pages:", response);
      }
    } catch (error) {
      console.error("Error fetching pages:", error);
    } finally {
      setIsLoading(false);
    }
  }, [website?.id, limit, fetchAllPageSpeedData]);

  // Handle page change for pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchPages(newPage);
    }
  };

  // Fetch pages when website changes
  useEffect(() => {
    if (website?.id) {
      fetchPages(1);
      fetchAllPagesForValidation();
    }
  }, [website?.id, fetchPages, fetchAllPagesForValidation]);
  
  // Handle new page form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length === 0) {
      try {
        // Process URL to ensure it has protocol
        let url = newPage.url;
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = `https://${url}`;
        }
        
        // Use pageService.createPage instead of websiteService.addWebsitePage
        const response = await pageService.createPage({
          ...newPage,
          url,
          website_id: website.id // Associate the page with the website
        });
        
        if (response.success) {
          setIsModalOpen(false);
          setNewPage({ url: '', title: '', description: '' });
          fetchPages(1); // This will also refresh performance data
          // Also refresh the validation list
          fetchAllPagesForValidation();
        } else {
          setErrors({ submit: response.message || 'Failed to add page' });
        }
      } catch (error) {
        console.error("Error adding page:", error);
        setErrors({ submit: error.message || 'An error occurred while adding the page' });
      }
    } else {
      setErrors(validationErrors);
    }
  };
  
  // Validate form inputs
  const validateForm = () => {
    const errors = {};
    
    // URL validation
    if (!newPage.url.trim()) {
      errors.url = 'URL is required';
    } else {
      // Basic URL validation
      const urlPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
      if (!urlPattern.test(newPage.url)) {
        errors.url = 'Please enter a valid URL';
      } else {
        // Format URLs for comparison
        let formattedPageUrl = newPage.url;
        if (!formattedPageUrl.startsWith('http://') && !formattedPageUrl.startsWith('https://')) {
          formattedPageUrl = `https://${formattedPageUrl}`;
        }
        
        let rootUrl = website?.root_url || '';
        if (!rootUrl.startsWith('http://') && !rootUrl.startsWith('https://')) {
          rootUrl = `https://${rootUrl}`;
        }

        // Properly extract and compare hostnames
        const getHostname = (url) => {
          try {
            return new URL(url).hostname.toLowerCase();
          } catch (e) {
            console.error("Error parsing URL:", e);
            return url.toLowerCase(); // Fallback
          }
        };
        
        const pageHostname = getHostname(formattedPageUrl);
        const rootHostname = getHostname(rootUrl);
        
        // Check if page hostname is the same as or a subdomain of the root hostname
        const isValidSubdomain = pageHostname === rootHostname || 
          (pageHostname.endsWith(`.${rootHostname}`) && pageHostname !== rootHostname);
          
        // If not matching the exact hostname or not a valid subdomain
        if (!isValidSubdomain) {
          errors.url = `URL must be under the same domain as the website (${rootHostname})`;
        }
        
        // Enhanced URL normalization for duplicate checking
        const normalizeUrl = (url) => {
          try {
            const urlObj = new URL(url);
            // Create a normalized version of the URL for comparison
            let normalized = urlObj.hostname.toLowerCase() + urlObj.pathname;
            // Remove trailing slash
            normalized = normalized.replace(/\/$/, '');
            // Remove www. prefix
            normalized = normalized.replace(/^www\./, '');
            
            return normalized;
          } catch(e) {
            // Fallback if URL parsing fails
            let normalized = url.toLowerCase()
              .replace(/^https?:\/\//i, '') // Remove protocol
              .replace(/^www\./i, '')       // Remove www.
              .replace(/\/$/, '');          // Remove trailing slash
              
            // Handle potential query parameters and fragments
            return normalized.split(/[?#]/)[0];
          }
        };
        
        const normalizedNewUrl = normalizeUrl(formattedPageUrl);
        
        // Check for duplicate URLs using the complete list of pages with enhanced normalization
        const isDuplicate = allPagesForValidation.some(page => {
          const normalizedExistingUrl = normalizeUrl(page.url);
          return normalizedExistingUrl === normalizedNewUrl;
        });
        
        if (isDuplicate) {
          errors.url = 'This page URL already exists for this website';
        }
      }
    }
    
    if (!newPage.title.trim()) {
      errors.title = 'Title is required';
    }
    
    return errors;
  };
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPage(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  // Handle deleting a page
  const handleDeletePage = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) {
      return;
    }
    
    try {
      // Use pageService.deletePage instead of websiteService.deleteWebsitePage
      const response = await pageService.deletePage(pageId);
      
      if (response.success) {
        // Re-fetch the current page, or go to page 1 if we deleted the only item on this page
        const shouldGoToFirstPage = pages.length === 1 && currentPage > 1;
        fetchPages(shouldGoToFirstPage ? 1 : currentPage);
        // Also refresh the validation list
        fetchAllPagesForValidation();
      } else {
        console.error("Failed to delete page:", response);
      }
    } catch (error) {
      console.error("Error deleting page:", error);
    }
  };
  
  // Open the result modal with the selected page - use data from our pre-fetched map if possible
  const openResultModal = async (page) => {
    // Reset device type to desktop when opening a new modal
    setActiveDeviceType('desktop');
    
    // Find if we already have detailed data for this page
    const existingPageWithData = pagesWithPerformanceData.find(p => p.id === page.id);
    const hasDesktopData = existingPageWithData && existingPageWithData.desktop_data;
    const hasMobileData = existingPageWithData && existingPageWithData.mobile_data;
    
    // If we have both desktop and mobile data locally, use them
    if (existingPageWithData && (hasDesktopData || hasMobileData)) {
      try {
        // Prepare the page object with combined desktop/mobile data
        const enhancedPage = {
          ...existingPageWithData,
          // Set the active device type data as the default
          performance_score: hasDesktopData ? existingPageWithData.desktop_data.performance_score : 
                              hasMobileData ? existingPageWithData.mobile_data.performance_score : null,
          last_analyzed_at: hasDesktopData ? existingPageWithData.desktop_data.last_analyzed_at : 
                             hasMobileData ? existingPageWithData.mobile_data.last_analyzed_at : null,
          raw_metrics: hasDesktopData ? existingPageWithData.desktop_data.raw_metrics :
                        hasMobileData ? existingPageWithData.mobile_data.raw_metrics : null,
          // Parse raw_data for the initial device type (desktop by default)
          raw_data: parseRawData(hasDesktopData ? existingPageWithData.desktop_data.raw_data : 
                                hasMobileData ? existingPageWithData.mobile_data.raw_data : null)
        };
        
        setSelectedPage(enhancedPage);
      } catch (error) {
        console.error("Error parsing page data:", error);
        setSelectedPage(existingPageWithData);
      }
    } else {
      // We don't have the data locally, so fetch it from the API
      try {
        // Need to fetch fresh data for this specific page
        const performanceData = await pageService.getPageSpeedData(page.id);
        
        if (performanceData && performanceData.success) {
          // Separate data by device type
          const desktopData = performanceData.data?.filter(data => !data.is_mobile)
            .sort((a, b) => new Date(b.analyzed_at) - new Date(a.analyzed_at))[0];
          const mobileData = performanceData.data?.filter(data => data.is_mobile)
            .sort((a, b) => new Date(b.analyzed_at) - new Date(a.analyzed_at))[0];

          // Use desktop data by default, fall back to mobile if needed
          const primaryData = desktopData || mobileData || {};

          try {
            const pageWithData = {
              ...page,
              performance_score: primaryData?.performance_score || null,
              last_analyzed_at: primaryData?.analyzed_at || null,
              raw_metrics: primaryData?.raw_metrics || null,
              raw_data: parseRawData(primaryData?.raw_data),
              // Store both sets of data separately
              desktop_data: desktopData ? {
                performance_score: desktopData.performance_score,
                last_analyzed_at: desktopData.analyzed_at,
                raw_metrics: desktopData.raw_metrics,
                raw_data: parseRawData(desktopData.raw_data)
              } : null,
              mobile_data: mobileData ? {
                performance_score: mobileData.performance_score,
                last_analyzed_at: mobileData.analyzed_at, 
                raw_metrics: mobileData.raw_metrics,
                raw_data: parseRawData(mobileData.raw_data)
              } : null
            };
            
            setSelectedPage(pageWithData);
          } catch (error) {
            console.error("Error parsing raw_data from API:", error);
            
            // Fallback to unparsed data
            const pageWithData = {
              ...page,
              performance_score: primaryData?.performance_score || null,
              last_analyzed_at: primaryData?.analyzed_at || null,
              raw_metrics: primaryData?.raw_metrics || null,
              desktop_data: desktopData || null,
              mobile_data: mobileData || null
            };
            
            setSelectedPage(pageWithData);
          }
        } else {
          setSelectedPage(page);
        }
      } catch (error) {
        console.error(`Error fetching detailed performance data for page ${page.id}:`, error);
        setSelectedPage(page);
      }
    }
    
    setIsResultModalOpen(true);
  };

  // Helper function to parse raw_data JSON
  const parseRawData = (rawData) => {
    if (!rawData) return null;
    
    try {
      if (typeof rawData === 'string') {
        const firstParse = JSON.parse(rawData);
        // Handle case where raw_data might be a nested JSON string
        if (typeof firstParse.raw_data === 'string') {
          return JSON.parse(firstParse.raw_data);
        } else {
          return firstParse;
        }
      } else {
        return rawData;
      }
    } catch (error) {
      console.error("Error parsing raw_data:", error);
      return rawData; // Return original if parsing fails
    }
  };
  
  // Toggle between desktop and mobile data
  const toggleDeviceType = (deviceType) => {
    if (deviceType === activeDeviceType || !selectedPage) return;
    
    setActiveDeviceType(deviceType);
    
    // Update displayed data based on selected device type
    if (deviceType === 'desktop' && selectedPage.desktop_data) {
      setSelectedPage({
        ...selectedPage,
        performance_score: selectedPage.desktop_data.performance_score,
        last_analyzed_at: selectedPage.desktop_data.last_analyzed_at,
        raw_metrics: selectedPage.desktop_data.raw_metrics,
        raw_data: parseRawData(selectedPage.desktop_data.raw_data)
      });
    } else if (deviceType === 'mobile' && selectedPage.mobile_data) {
      setSelectedPage({
        ...selectedPage,
        performance_score: selectedPage.mobile_data.performance_score,
        last_analyzed_at: selectedPage.mobile_data.last_analyzed_at,
        raw_metrics: selectedPage.mobile_data.raw_metrics,
        raw_data: parseRawData(selectedPage.mobile_data.raw_data)
      });
    }
  };

  // Handle running page-specific analysis
  const handleRunPageAnalysis = async (page) => {
    if (!page || !page.url) {
      console.error("Cannot run PageSpeed analysis: Invalid page data", page);
      return;
    }
    
    // Create complete page object needed for analysis
    const pageForAnalysis = {
      url: page.url,
      id: page.id,
      website_id: website.id
    };
    
    try {
      // Call the custom runPageAnalysis function from props if it exists
      if (typeof runPageAnalysis === 'function') {
        await runPageAnalysis(pageForAnalysis);
      }
    } catch (error) {
      console.error("Error running page analysis:", error);
      
      // Create a user-friendly error message
      let errorMessage = "We encountered a problem while analyzing this page.";
      
      if (error.message) {
        if (error.message.includes("timeout") || error.message.includes("timed out")) {
          errorMessage = "The analysis timed out. The page may be too large or the server is experiencing high load.";
        } else if (error.message.includes("network") || error.message.includes("connection")) {
          errorMessage = "Network error: Unable to connect to the page. Please check if the URL is accessible.";
        } else if (error.message.includes("404") || error.message.includes("not found")) {
          errorMessage = "The page was not found. Please verify that the URL is correct.";
        } else if (error.message.includes("403") || error.message.includes("forbidden")) {
          errorMessage = "Access to the page is forbidden. The website may be blocking our analysis tools.";
        } else if (error.message.includes("500") || error.message.includes("server error")) {
          errorMessage = "The website returned a server error. Please try again later.";
        }
      }
      
      // Use the showToast function passed as a prop from the parent component
      if (showToast) {
        showToast(errorMessage, 'error');
      } else {
        // Fallback notification if showToast function isn't available
        alert(errorMessage);
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold flex items-center">
          <Globe className="h-5 w-5 mr-2 text-blue-600" />
          Website Pages
        </h2>
        
        <button
          onClick={() => setIsModalOpen(true)}
          className="text-sm text-white bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded-md flex items-center gap-1"
        >
          <Plus className="h-3 w-3" />
          Add Page
        </button>
      </div>
      
      {/* Add Page Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add New Page</h3>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Page URL
                </label>
                <input
                  type="text"
                  name="url"
                  value={newPage.url}
                  onChange={handleInputChange}
                  placeholder="https://example.com/page"
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.url ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.url && (
                  <p className="text-red-500 text-sm mt-1">{errors.url}</p>
                )}
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Page Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={newPage.title}
                  onChange={handleInputChange}
                  placeholder="Page Title"
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  name="description"
                  value={newPage.description}
                  onChange={handleInputChange}
                  placeholder="Page description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="3"
                />
              </div>
              
              {errors.submit && (
                <p className="text-red-500 text-sm mb-4">{errors.submit}</p>
              )}
              
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  Add Page
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Page Analysis Result Modal */}
      {isResultModalOpen && selectedPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold flex items-center">
                <BarChart2 className="h-5 w-5 mr-2 text-blue-600" />
                PageSpeed Analysis: {selectedPage.title || selectedPage.url}
              </h3>
              <button 
                onClick={() => setIsResultModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-4 pb-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <a 
                  href={selectedPage.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-1"
                >
                  {selectedPage.url}
                  <ExternalLink className="h-4 w-4" />
                </a>
                {selectedPage.raw_data?.finalUrl !== selectedPage.raw_data?.requestedUrl && (
                  <div className="text-amber-600 text-sm flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4" />
                    Redirected URL
                  </div>
                )}
              </div>
              
              <div className="flex items-center justify-between mt-1">
                <p className="text-sm text-gray-500">
                  Last analyzed: {formatDate(selectedPage.last_analyzed_at)}
                </p>
                {selectedPage.raw_data?.fetchTime && (
                  <p className="text-xs text-gray-500">
                    Fetch time: {new Date(selectedPage.raw_data.fetchTime).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            
            {/* Device Type Toggles */}
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <h4 className="text-md font-medium">Device Type</h4>
                <div className="bg-gray-100 rounded-lg p-1 flex items-center">
                  <button
                    onClick={() => toggleDeviceType('desktop')}
                    className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                      activeDeviceType === 'desktop'
                        ? 'bg-white text-blue-700 shadow-sm font-medium'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    disabled={!selectedPage.desktop_data}
                  >
                    Desktop
                    {!selectedPage.desktop_data && <span className="ml-1 text-xs text-gray-400">(No data)</span>}
                  </button>
                  <button
                    onClick={() => toggleDeviceType('mobile')}
                    className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                      activeDeviceType === 'mobile'
                        ? 'bg-white text-blue-700 shadow-sm font-medium'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    disabled={!selectedPage.mobile_data}
                  >
                    Mobile
                    {!selectedPage.mobile_data && <span className="ml-1 text-xs text-gray-400">(No data)</span>}
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {activeDeviceType === 'desktop' 
                  ? "Desktop analysis simulates a high-performance desktop environment." 
                  : "Mobile analysis simulates a mid-tier mobile device with mobile network throttling."}
              </p>
            </div>
            
            {/* Lighthouse Info & Warnings */}
            {selectedPage.raw_data && (
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  {selectedPage.raw_data.lighthouseVersion && (
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      Lighthouse v{selectedPage.raw_data.lighthouseVersion}
                    </span>
                  )}
                  {selectedPage.raw_data.configSettings?.formFactor && (
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                      {selectedPage.raw_data.configSettings.formFactor.charAt(0).toUpperCase() + 
                       selectedPage.raw_data.configSettings.formFactor.slice(1)} Device
                    </span>
                  )}
                </div>
                
                {/* Warnings */}
                {selectedPage.raw_data.runWarnings && selectedPage.raw_data.runWarnings.length > 0 && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <h5 className="text-sm font-medium text-amber-800 mb-2">Analysis Warnings</h5>
                    <ul className="list-disc pl-5 space-y-1">
                      {selectedPage.raw_data.runWarnings.map((warning, index) => (
                        <li key={index} className="text-sm text-amber-700">
                          {warning}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {/* Performance Score */}
            <div className="mb-6">
              <h4 className="text-md font-semibold mb-3">Performance Score</h4>
              <div className="flex items-center gap-3">
                <div className="h-3 w-24 rounded-full bg-gray-200">
                  <div
                    className={`h-3 rounded-full ${
                      selectedPage.performance_score >= 90
                        ? "bg-green-500"
                        : selectedPage.performance_score >= 50
                        ? "bg-yellow-500"
                        : "bg-red-500"
                    }`}
                    style={{ width: `${selectedPage.performance_score}%` }}
                  ></div>
                </div>
                <span className={`font-medium ${
                  selectedPage.performance_score >= 90
                    ? "text-green-700"
                    : selectedPage.performance_score >= 50
                    ? "text-yellow-700"
                    : "text-red-700"
                }`}>
                  {selectedPage.performance_score}
                </span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  selectedPage.performance_score >= 90
                    ? "bg-green-100 text-green-800"
                    : selectedPage.performance_score >= 50
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-red-100 text-red-800"
                }`}>
                  {selectedPage.performance_score >= 90 ? "Fast" : 
                   selectedPage.performance_score >= 50 ? "Moderate" : "Slow"}
                </span>
              </div>
            </div>
            
            {/* Lighthouse Metrics */}
            {selectedPage.raw_data?.audits && (
              <div className="mb-6">
                <h4 className="text-md font-semibold mb-3">Key Lighthouse Metrics</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* First Contentful Paint */}
                  {selectedPage.raw_data.audits['first-contentful-paint'] && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-1">
                        <p className="text-sm text-gray-700 font-medium">First Contentful Paint</p>
                        {selectedPage.raw_data.audits['first-contentful-paint'].score !== null && (
                          <span className={`h-6 w-6 flex items-center justify-center rounded-full text-xs font-medium
                            ${selectedPage.raw_data.audits['first-contentful-paint'].score >= 0.9 
                              ? 'bg-green-100 text-green-800'
                              : selectedPage.raw_data.audits['first-contentful-paint'].score >= 0.5 
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'}`
                          }>
                            {Math.round(selectedPage.raw_data.audits['first-contentful-paint'].score * 100)}
                          </span>
                        )}
                      </div>
                      <p className="text-xl font-semibold">
                        {selectedPage.raw_data.audits['first-contentful-paint'].displayValue || 
                         (selectedPage.raw_data.audits['first-contentful-paint'].numericValue / 1000).toFixed(2) + 's'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Time until the browser renders the first bit of content
                      </p>
                    </div>
                  )}

                  {/* Largest Contentful Paint */}
                  {selectedPage.raw_data.audits['largest-contentful-paint'] && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-1">
                        <p className="text-sm text-gray-700 font-medium">Largest Contentful Paint</p>
                        {selectedPage.raw_data.audits['largest-contentful-paint'].score !== null && (
                          <span className={`h-6 w-6 flex items-center justify-center rounded-full text-xs font-medium
                            ${selectedPage.raw_data.audits['largest-contentful-paint'].score >= 0.9 
                              ? 'bg-green-100 text-green-800'
                              : selectedPage.raw_data.audits['largest-contentful-paint'].score >= 0.5 
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'}`
                          }>
                            {Math.round(selectedPage.raw_data.audits['largest-contentful-paint'].score * 100)}
                          </span>
                        )}
                      </div>
                      <p className="text-xl font-semibold">
                        {selectedPage.raw_data.audits['largest-contentful-paint'].displayValue || 
                         (selectedPage.raw_data.audits['largest-contentful-paint'].numericValue / 1000).toFixed(2) + 's'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Time until the largest text or image is painted
                      </p>
                    </div>
                  )}

                  {/* Total Blocking Time */}
                  {selectedPage.raw_data.audits['total-blocking-time'] && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-1">
                        <p className="text-sm text-gray-700 font-medium">Total Blocking Time</p>
                        {selectedPage.raw_data.audits['total-blocking-time'].score !== null && (
                          <span className={`h-6 w-6 flex items-center justify-center rounded-full text-xs font-medium
                            ${selectedPage.raw_data.audits['total-blocking-time'].score >= 0.9 
                              ? 'bg-green-100 text-green-800'
                              : selectedPage.raw_data.audits['total-blocking-time'].score >= 0.5 
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'}`
                          }>
                            {Math.round(selectedPage.raw_data.audits['total-blocking-time'].score * 100)}
                          </span>
                        )}
                      </div>
                      <p className="text-xl font-semibold">
                        {selectedPage.raw_data.audits['total-blocking-time'].displayValue || 
                         selectedPage.raw_data.audits['total-blocking-time'].numericValue + 'ms'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Total time when the main thread was blocked from responding to input
                      </p>
                    </div>
                  )}

                  {/* Cumulative Layout Shift */}
                  {selectedPage.raw_data.audits['cumulative-layout-shift'] && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-1">
                        <p className="text-sm text-gray-700 font-medium">Cumulative Layout Shift</p>
                        {selectedPage.raw_data.audits['cumulative-layout-shift'].score !== null && (
                          <span className={`h-6 w-6 flex items-center justify-center rounded-full text-xs font-medium
                            ${selectedPage.raw_data.audits['cumulative-layout-shift'].score >= 0.9 
                              ? 'bg-green-100 text-green-800'
                              : selectedPage.raw_data.audits['cumulative-layout-shift'].score >= 0.5 
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'}`
                          }>
                            {Math.round(selectedPage.raw_data.audits['cumulative-layout-shift'].score * 100)}
                          </span>
                        )}
                      </div>
                      <p className="text-xl font-semibold">
                        {selectedPage.raw_data.audits['cumulative-layout-shift'].displayValue || 
                         selectedPage.raw_data.audits['cumulative-layout-shift'].numericValue}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Measures visual stability as the page loads
                      </p>
                    </div>
                  )}
                  
                  {/* Speed Index */}
                  {selectedPage.raw_data.audits['speed-index'] && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-1">
                        <p className="text-sm text-gray-700 font-medium">Speed Index</p>
                        {selectedPage.raw_data.audits['speed-index'].score !== null && (
                          <span className={`h-6 w-6 flex items-center justify-center rounded-full text-xs font-medium
                            ${selectedPage.raw_data.audits['speed-index'].score >= 0.9 
                              ? 'bg-green-100 text-green-800'
                              : selectedPage.raw_data.audits['speed-index'].score >= 0.5 
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'}`
                          }>
                            {Math.round(selectedPage.raw_data.audits['speed-index'].score * 100)}
                          </span>
                        )}
                      </div>
                      <p className="text-xl font-semibold">
                        {selectedPage.raw_data.audits['speed-index'].displayValue || 
                         (selectedPage.raw_data.audits['speed-index'].numericValue / 1000).toFixed(2) + 's'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        How quickly content is visually displayed during page load
                      </p>
                    </div>
                  )}
                  
                  {/* Time to Interactive */}
                  {selectedPage.raw_data.audits['interactive'] && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-1">
                        <p className="text-sm text-gray-700 font-medium">Time to Interactive</p>
                        {selectedPage.raw_data.audits['interactive'].score !== null && (
                          <span className={`h-6 w-6 flex items-center justify-center rounded-full text-xs font-medium
                            ${selectedPage.raw_data.audits['interactive'].score >= 0.9 
                              ? 'bg-green-100 text-green-800'
                              : selectedPage.raw_data.audits['interactive'].score >= 0.5 
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'}`
                          }>
                            {Math.round(selectedPage.raw_data.audits['interactive'].score * 100)}
                          </span>
                        )}
                      </div>
                      <p className="text-xl font-semibold">
                        {selectedPage.raw_data.audits['interactive'].displayValue || 
                         (selectedPage.raw_data.audits['interactive'].numericValue / 1000).toFixed(2) + 's'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Time until the page becomes fully interactive
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Key Opportunities and Diagnostics */}
            {selectedPage.raw_data?.audits && (
              <div className="mb-6">
                <h4 className="text-md font-semibold mb-3">Improvement Opportunities</h4>
                <div className="space-y-3">
                  {/* Low-scoring audits that represent opportunities */}
                  {[
                    'render-blocking-resources',
                    'unused-javascript',
                    'unused-css-rules',
                    'unminified-javascript',
                    'modern-image-formats',
                    'offscreen-images',
                    'uses-optimized-images',
                    'uses-text-compression',
                    'uses-responsive-images',
                    'legacy-javascript'
                  ].map(auditKey => {
                    const audit = selectedPage.raw_data.audits[auditKey];
                    if (!audit || audit.score === null || audit.score >= 0.9) return null;
                    
                    return (
                      <div key={auditKey} className="p-3 bg-white border border-gray-200 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <h5 className="font-medium text-gray-800">{audit.title}</h5>
                            <p className="text-sm text-gray-600 mt-1">{audit.description}</p>
                          </div>
                          {audit.displayValue && (
                            <span className="text-sm px-2 py-1 rounded bg-amber-50 text-amber-800 whitespace-nowrap">
                              {audit.displayValue}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Show if no opportunities were found */}
                  {!Object.keys(selectedPage.raw_data.audits)
                    .filter(key => [
                      'render-blocking-resources',
                      'unused-javascript',
                      'unused-css-rules',
                      'unminified-javascript',
                      'modern-image-formats',
                      'offscreen-images',
                      'uses-optimized-images',
                      'uses-text-compression',
                      'uses-responsive-images',
                      'legacy-javascript'
                    ].includes(key) && 
                    selectedPage.raw_data.audits[key] && 
                    selectedPage.raw_data.audits[key].score !== null &&
                    selectedPage.raw_data.audits[key].score < 0.9).length && (
                    <div className="p-4 bg-green-50 border border-green-100 rounded-lg flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <p className="text-green-700">No major performance issues detected!</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Original Raw Metrics Section */}
            {selectedPage.raw_metrics && (
              <div className="mb-6">
                <h4 className="text-md font-semibold mb-3">Additional Metrics</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(() => {
                    try {
                      const metrics = JSON.parse(selectedPage.raw_metrics);
                      
                      return (
                        <>
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">First Paint</p>
                            <p className="text-xl font-semibold">
                              {(metrics.first_paint / 1000).toFixed(2)}s
                            </p>
                            <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                              <div 
                                className={`h-1.5 rounded-full ${metrics.first_paint < 1000 ? 'bg-green-500' : metrics.first_paint < 2000 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.min(100, metrics.first_paint/50)}%` }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Page Load</p>
                            <p className="text-xl font-semibold">
                              {(metrics.load_time / 1000).toFixed(2)}s
                            </p>
                            <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                              <div 
                                className={`h-1.5 rounded-full ${metrics.load_time < 3000 ? 'bg-green-500' : metrics.load_time < 6000 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.min(100, metrics.load_time/100)}%` }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Requests</p>
                            <p className="text-xl font-semibold">
                              {metrics.requests || 'N/A'}
                            </p>
                            <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                              <div 
                                className={`h-1.5 rounded-full ${metrics.requests < 50 ? 'bg-green-500' : metrics.requests < 100 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.min(100, metrics.requests * 0.8)}%` }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">CPU Load</p>
                            <p className="text-xl font-semibold">
                              {metrics.cpu_load_percent || 0}%
                            </p>
                            <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                              <div 
                                className={`h-1.5 rounded-full ${metrics.cpu_load_percent < 30 ? 'bg-green-500' : metrics.cpu_load_percent < 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${metrics.cpu_load_percent || 0}%` }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Memory Usage</p>
                            <p className="text-xl font-semibold">
                              {metrics.memory_usage_mb || 0} MB
                            </p>
                            <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                              <div 
                                className={`h-1.5 rounded-full ${metrics.memory_usage_mb < 200 ? 'bg-green-500' : metrics.memory_usage_mb < 400 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.min(100, (metrics.memory_usage_mb || 0) / 5)}%` }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Transfer Size</p>
                            <p className="text-xl font-semibold">
                              {formatBytes(metrics.transfer_size || 0)}
                            </p>
                            <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                              <div 
                                className={`h-1.5 rounded-full ${metrics.transfer_size < 1000000 ? 'bg-green-500' : metrics.transfer_size < 3000000 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.min(100, (metrics.transfer_size || 0) / 50000)}%` }}
                              ></div>
                            </div>
                          </div>
                        </>
                      );
                    } catch (e) {
                      console.error("Error parsing metrics:", e);
                      return (
                        <div className="col-span-3 p-4 bg-gray-50 rounded-lg text-center">
                          <p className="text-gray-500">Error loading metrics data</p>
                          <p className="text-xs text-gray-400 mt-1">Raw data: {selectedPage.raw_metrics}</p>
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => setIsResultModalOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
              <button
                onClick={() => handleRunPageAnalysis(selectedPage)}
                disabled={pageSpeedAnalysis.isRunning}
                className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${pageSpeedAnalysis.isRunning ? "animate-spin" : ""}`} />
                Re-analyze Page
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Pages Table */}
      {pages.length > 0 ? (
        <div>
          {/* PageSpeed Data Loading Indicator */}
          {isLoadingPageSpeedData && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-4 h-4 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin mr-2"></div>
                <span>Loading performance data...</span>
              </div>
              <span className="text-xs text-blue-600">This may take a moment</span>
            </div>
          )}
          
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border-b py-3 px-4 text-xs uppercase font-medium text-gray-700">Page URL</th>
                  <th className="border-b py-3 px-4 text-xs uppercase font-medium text-gray-700">Performance</th>
                  <th className="border-b py-3 px-4 text-xs uppercase font-medium text-gray-700">Last Analyzed</th>
                  <th className="border-b py-3 px-4 text-xs uppercase font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagesWithPerformanceData.map((page) => (
                  <tr key={page.id} className="hover:bg-gray-50">
                    <td className="border-b py-3 px-4">
                      <a 
                        href={page.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-1"
                      >
                        {page.url.replace(website?.root_url || "", "") || page.url}
                        <ExternalLink className="h-3 w-3 inline" />
                      </a>
                      <p className="text-xs text-gray-500 mt-1">
                        {page.title || "No title"}
                      </p>
                    </td>
                    <td className="border-b py-3 px-4">
                      {page.performance_score ? (
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-20 rounded-full bg-gray-200">
                            <div
                              className={`h-2 rounded-full ${
                                page.performance_score >= 90
                                  ? "bg-green-500"
                                  : page.performance_score >= 50
                                  ? "bg-yellow-500"
                                  : "bg-red-500"
                              }`}
                              style={{ width: `${page.performance_score}%` }}
                            ></div>
                          </div>
                          <span className={`font-medium ${
                            page.performance_score >= 90
                              ? "text-green-700"
                              : page.performance_score >= 50
                              ? "text-yellow-700"
                              : "text-red-700"
                          }`}>
                            {page.performance_score}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-500">Not analyzed</span>
                      )}
                    </td>
                    <td className="border-b py-3 px-4 text-gray-600">
                      {page.last_analyzed_at ? formatDate(page.last_analyzed_at) : "Never analyzed"}
                    </td>
                    <td className="border-b py-3 px-4">
                      <div className="flex gap-2">
                        {/* View Results Button */}
                        <button
                          onClick={() => openResultModal(page)}
                          disabled={!page.last_analyzed_at}
                          title="View Analysis Results"
                          className={`p-2 rounded-md ${
                            !page.last_analyzed_at 
                              ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                              : 'text-blue-600 hover:bg-blue-100'
                          }`}
                        >
                          <BarChart2 className="h-4 w-4" />
                          <span className="sr-only">View Analysis Results</span>
                        </button>

                        {/* Analyze/Re-analyze Button */}
                        <button
                          onClick={() => handleRunPageAnalysis(page)}
                          disabled={pageSpeedAnalysis.isRunning}
                          title={page.last_analyzed_at ? "Re-analyze Page" : "Analyze Page"}
                          className={`p-2 rounded-md ${
                            pageSpeedAnalysis.isRunning
                              ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                              : 'text-green-600 hover:bg-green-100'
                          }`}
                        >
                          <RefreshCw className={`h-4 w-4 ${pageSpeedAnalysis.isRunning && "animate-spin"}`} />
                          <span className="sr-only">{page.last_analyzed_at ? "Re-analyze" : "Analyze"}</span>
                        </button>

                        {/* Delete Page Button */}
                        <button
                          onClick={() => handleDeletePage(page.id)}
                          title="Delete Page"
                          className="p-2 rounded-md text-red-600 hover:bg-red-100"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Delete Page</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{((currentPage - 1) * limit) + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * limit, (currentPage - 1) * limit + pages.length)}
                </span>{' '}
                of <span className="font-medium">{totalItems}</span> pages
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-md ${
                    currentPage === 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-md ${
                    currentPage === totalPages
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : isLoading ? (
        <div className="py-10 text-center">
          <div className="inline-flex items-center">
            <div className="w-6 h-6 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin mr-2"></div>
            <span className="text-gray-600">Loading pages...</span>
          </div>
        </div>
      ) : (
        <div className="py-8 text-center">
          <Globe className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-2">No pages found for this website</p>
          <p className="text-sm text-gray-400 mb-4">
            Add pages to start tracking their performance
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Page
          </button>
        </div>
      )}
    </div>
  );
};

export default WebsitePages;